import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import logger from '../../lib/logger';
import { useTranslation } from '../../i18n/LanguageProvider';

interface OrganizationAccessGateProps {
  children: React.ReactNode;
}

const OrganizationAccessGate: React.FC<OrganizationAccessGateProps> = ({ children }) => {
  const { user, profile, organization, role, isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isChecking, setIsChecking] = useState(true);

  // Simulate a brief loading state for smooth UX
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsChecking(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Show loading state
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading') || 'Laddar...'}</p>
        </div>
      </div>
    );
  }

  // If not authenticated, let the route handle the redirect
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // Check if user has an organization
  if (!organization) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-8 border border-gray-200 dark:border-gray-700">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {t('organization.noOrganization') || 'Ingen organisation hittades'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {t('organization.noOrganizationDesc') || 'Din användare är inte kopplad till någon organisation i systemet.'}
              </p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-xs text-blue-800 dark:text-blue-200">
                <strong>{t('organization.whatDoesThisMean') || 'Vad betyder detta?'}</strong><br />
                {t('organization.noOrganizationHelp') || 'Du behöver bli inbjuden av en administratör för att få tillgång till kundbasen.'}
              </p>
            </div>
            <button
              onClick={() => {
                logger.audit('Användare utloggad från OrganizationAccessGate (ingen org)', {});
                window.location.href = '/login?error=no_organization';
              }}
              className="w-full py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('common.logout') || 'Logga ut'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user has a valid role
  const validRoles = ['admin', 'technician', 'agent', 'viewer'];
  if (!role || !validRoles.includes(role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-8 border border-gray-200 dark:border-gray-700">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {t('organization.roleMissing') || 'Roll saknas'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {t('organization.roleMissingDesc') || 'Din användare saknar en giltig roll i organisationen.'}
              </p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-xs text-red-800 dark:text-red-200">
                <strong>{t('organization.actionRequired') || 'Åtgärd krävs:'}</strong><br />
                {t('organization.roleMissingHelp') || 'Kontakta din organisationsadministratör för att få en roll tilldelad (admin, technician, agent eller viewer).'}
              </p>
            </div>
            <button
              onClick={() => {
                logger.audit('Användare utloggad från OrganizationAccessGate (ingen roll)', {});
                window.location.href = '/login?error=no_role';
              }}
              className="w-full py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('common.logout') || 'Logga ut'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User has both organization and role - render children
  return <>{children}</>;
};

export default OrganizationAccessGate;
